import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PostCard from '../components/PostCard';
import CommentDrawer from '../components/CommentDrawer';
import api, { fileUrl } from '../lib/api';
import { Link } from 'react-router-dom';
import { TrendingUp, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

export const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeComment, setActiveComment] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState([]);

  const load = async () => {
    try {
      const [feed, sug, trend] = await Promise.all([
        api.get('/posts/feed'),
        api.get('/users/suggestions'),
        api.get('/posts/trending'),
      ]);
      setPosts(feed.data);
      setSuggestions(sug.data);
      setTrending(trend.data.slice(0, 5));
    } catch {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFollow = async (userId) => {
    try {
      await api.post(`/users/${userId}/follow`);
      setSuggestions(s => s.filter(u => u.user_id !== userId));
      toast.success('Following');
    } catch {
      toast.error('Follow failed');
    }
  };

  const rightSidebar = (
    <div className="space-y-6">
      {/* Suggestions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Suggested for you</h3>
        </div>
        <div className="space-y-3">
          {suggestions.length === 0 && (
            <p className="text-[#52525B] text-sm">No suggestions right now.</p>
          )}
          {suggestions.map(u => (
            <div key={u.user_id} className="flex items-center gap-3" data-testid={`suggest-${u.username}`}>
              <Link to={`/profile/${u.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <img
                  src={u.avatar_path ? fileUrl(u.avatar_path) : avatarFallback(u.name)}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <div className="text-white text-sm font-bold font-heading truncate">{u.name}</div>
                  <div className="text-[#A1A1AA] text-xs truncate">@{u.username}</div>
                </div>
              </Link>
              <button
                onClick={() => handleFollow(u.user_id)}
                className="text-xs font-bold text-[#FFB000] hover:text-[#E69E00] flex items-center gap-1"
                data-testid={`follow-${u.username}`}
              >
                <UserPlus size={14} />
                Follow
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Trending */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={14} className="text-[#FFB000]" />
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Trending Now</h3>
        </div>
        <div className="space-y-3">
          {trending.map((p, i) => (
            <Link key={p.id} to={`/profile/${p.author?.user_id}`} className="flex items-center gap-3 group">
              <span className="text-[#FFB000] font-heading font-black text-2xl w-6 flex-shrink-0">{i + 1}</span>
              <img
                src={p.cover_path ? fileUrl(p.cover_path) : 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=100'}
                alt=""
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="text-white text-sm font-bold truncate group-hover:text-[#FFB000] transition-colors font-heading">
                  {p.title}
                </div>
                <div className="text-[#A1A1AA] text-xs truncate">
                  {p.author?.name} · {p.likes_count} likes
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Layout rightSidebar={rightSidebar}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Feed</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          Fresh on the decks
        </h1>
      </motion.div>

      {loading ? (
        <div className="space-y-6">
          {[0, 1, 2].map(i => (
            <div key={i} className="post-card p-6 animate-pulse h-96" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="post-card p-12 text-center">
          <h3 className="font-heading font-bold text-2xl text-white mb-2">Your feed is quiet.</h3>
          <p className="text-[#A1A1AA] mb-6">Upload your first mix or follow some DJs to get things moving.</p>
          <Link to="/upload" className="btn-pill btn-primary inline-flex items-center gap-2" data-testid="empty-upload-cta">
            Upload a Mix
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onCommentOpen={setActiveComment}
            />
          ))}
        </div>
      )}
      <CommentDrawer post={activeComment} onClose={() => setActiveComment(null)} />
    </Layout>
  );
};

export default Feed;
